import { DateTime } from 'luxon'
import { useMemo } from 'react'
import { useNow } from '../../../hooks/use-now.ts'
import type { BeaconSummary, CoverageVerdictLevel } from '../../../utils/api-client/api-schemas.ts'
import {
  type BeaconRunState,
  displayedVerdictLevel,
  footerStats,
  isRunFinished,
  keptTotal,
  pillFromState,
  STAGES,
} from '../beacon-run.ts'
import { BeaconScanningPill } from '../beacon-scanning-pill.tsx'
import * as styles from './beacon-live-view.css.ts'
import { BeaconNarrativeLog, formatDuration } from './beacon-narrative-log.tsx'
import { BeaconSurface, type SurfaceVariant } from './beacon-surface.tsx'

const formatMillions = (tokens: number) => `${(tokens / 1e6).toFixed(1)}M`

export type BeaconLiveViewProps = {
  run: BeaconRunState
  settledSeq: number
  repositoryName?: string
  branch?: string
  /** The stored summary, once the run has finished: alarms and evidence for the verdict. */
  summary?: BeaconSummary | null
  /** The cancel control, shown while the run is in flight. */
  cancelControl?: React.ReactNode
  initialVariant?: SurfaceVariant
  /** Fixes the clock, for stories and tests. */
  nowMs?: number
}

/**
 * The live view of a Beacon run, drawn entirely from the run state the event stream folds into: the
 * stage pipeline and budget, the attack surface lighting up, the narration, and — once the run is done —
 * the verdict on how much of the threat model it examined.
 */
export const BeaconLiveView: React.FC<BeaconLiveViewProps> = ({
  run,
  settledSeq,
  repositoryName,
  branch,
  summary,
  cancelControl,
  initialVariant,
  nowMs,
}) => {
  const isFinished = isRunFinished(run)
  const now = useNow({ isTicking: !isFinished && nowMs === undefined })
  const dismissedById = useMemo(
    () => new Map((summary?.dismissed ?? []).map(lead => [lead.finding_uuid, lead])),
    [summary]
  )
  const clock = isFinished ? DateTime.fromISO(run.ts ?? '').toMillis() : (nowMs ?? now)
  const elapsed = run.startTs ? clock - DateTime.fromISO(run.startTs).toMillis() : 0

  return (
    <div className={styles.view}>
      <header className={styles.top}>
        <div className={styles.titlebar}>
          <div>
            <p className={styles.eyebrow}>
              Pixee Beacon{repositoryName ? ` · ${repositoryName}` : ''}
              {branch ? ` · ${branch}` : ''}
            </p>
            <h2 className={styles.title}>Beacon scan</h2>
          </div>
          <div className={styles.titleActions}>
            {!isFinished && cancelControl}
            <BeaconScanningPill pill={pillFromState(run)} />
          </div>
        </div>
        <div className={styles.metaRow}>
          <StagePipeline run={run} clock={clock} />
          <Budget run={run} />
          <div className={styles.elapsed}>
            <span className={styles.elapsedLabel}>Elapsed</span>
            <span className={styles.bigNumber}>{formatDuration(elapsed)}</span>
          </div>
        </div>
      </header>
      <Verdict run={run} summary={summary} />
      <div className={styles.main}>
        <BeaconSurface run={run} dismissedById={dismissedById} initialVariant={initialVariant} />
        <BeaconNarrativeLog entries={run.log} startTs={run.startTs} settledSeq={settledSeq} />
      </div>
      <Footer run={run} summary={summary} />
    </div>
  )
}

const StagePipeline: React.FC<{ run: BeaconRunState; clock: number }> = ({ run, clock }) => (
  <ol className={styles.pipeline} aria-label="Scan stages">
    {STAGES.map(({ stage, label }) => {
      const progress = run.stages[stage]
      const endTs = progress.end ?? (isRunFinished(run) ? run.ts : undefined)
      const end = endTs ? DateTime.fromISO(endTs).toMillis() : clock
      const duration = progress.start ? formatDuration(end - DateTime.fromISO(progress.start).toMillis()) : ''
      return (
        <li key={stage} className={styles.stage} aria-label={`${label}: ${progress.state}`}>
          <div className={styles.stageBar({ state: progress.state })} />
          <div className={styles.stageLabel({ isRunning: progress.state === 'running' })}>
            <span>{label}</span>
            <span className={styles.tabular}>{duration}</span>
          </div>
        </li>
      )
    })}
  </ol>
)

const Budget: React.FC<{ run: BeaconRunState }> = ({ run }) => {
  const percent = run.budget ? Math.min(100, (run.spent / run.budget) * 100) : 0
  return (
    <div className={styles.budget}>
      <div className={styles.budgetRow}>
        <span>Token budget{run.effort ? ` · ${run.effort} effort` : ''}</span>
        <span className={styles.tabular}>
          {formatMillions(run.spent)} / {run.budget ? formatMillions(run.budget) : '—'}
        </span>
      </div>
      <div
        className={styles.track}
        role="progressbar"
        aria-label="Token budget used"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(percent)}
      >
        <div className={styles.budgetFill} style={{ width: `${percent}%` }} />
      </div>
    </div>
  )
}

const VERDICT_STATUS_LABELS: Record<CoverageVerdictLevel, string> = {
  on: 'Complete',
  degraded: 'Gaps',
  off: 'Not examined',
}

/**
 * The finished run's verdict. Worded only in terms of what was examined: a run with no verified findings says
 * what it looked at, never that nothing exploitable exists.
 */
const Verdict: React.FC<{ run: BeaconRunState; summary?: BeaconSummary | null }> = ({ run, summary }) => {
  if (!run.verdict) {
    return null
  }
  const level = displayedVerdictLevel(run.verdict, run.examinedPercent)
  const proof = summary?.coverage.proof
  return (
    <section className={styles.verdict({ level })} aria-label="Verdict">
      <div>
        <h2 className={styles.verdictTitle}>
          {run.verdict.headline ?? `Beacon examined ${Math.round(run.examinedPercent ?? 0)}% of the attack surface`}
          <span className={styles.level({ level })}>{VERDICT_STATUS_LABELS[level]}</span>
        </h2>
        <p className={styles.verdictText}>{run.verdict.sentence}</p>
        {proof && proof.alarms.length > 0 && (
          <ul className={styles.alarms} aria-label="Gaps">
            {proof.alarms.map(alarm => (
              <li key={alarm.kind}>{alarm.sentence}</li>
            ))}
          </ul>
        )}
      </div>
      <div className={styles.kpis}>
        <div className={styles.kpi}>
          <span className={styles.bigNumber}>{run.examinedPercent}%</span>
          <span className={styles.kpiLabel}>of the attack surface examined</span>
        </div>
        <div className={styles.kpi}>
          <span className={styles.bigNumber}>{keptTotal(run)}</span>
          <span className={styles.kpiLabel}>
            verified findings of {run.deduped ?? run.candidateOrder.length} suspected
          </span>
        </div>
        {proof && (
          <div className={styles.kpi}>
            <span className={styles.bigNumber}>{proof.files_read}</span>
            <span className={styles.kpiLabel}>distinct files read</span>
          </div>
        )}
      </div>
    </section>
  )
}

const Footer: React.FC<{ run: BeaconRunState; summary?: BeaconSummary | null }> = ({ run, summary }) => {
  const stats = footerStats(run)
  const distinctFiles = run.status === 'completed' ? summary?.coverage.proof.files_read : undefined
  const cells: readonly [React.ReactNode, string][] = [
    [stats.operations, 'operations mapped'],
    [`${stats.investigationsDone}/${stats.investigationsTotal || '—'}`, 'investigations'],
    [
      stats.candidatesDeduped !== undefined
        ? `${stats.candidatesRaw} → ${stats.candidatesDeduped}`
        : stats.candidatesRaw,
      stats.candidatesDeduped !== undefined ? 'candidates, deduplicated' : 'candidates',
    ],
    [stats.kept, 'verified findings'],
    [distinctFiles ?? stats.fileReads, distinctFiles !== undefined ? 'distinct files read' : 'file reads'],
  ]
  return (
    <section className={styles.foot} aria-label="Run totals">
      {cells.map(([value, label]) => (
        <div key={label} className={styles.footCell}>
          <span className={styles.bigNumber}>{value}</span>
          <span className={styles.footLabel}>{label}</span>
        </div>
      ))}
    </section>
  )
}
